import { Injectable } from '@angular/core';
import { KeyValue } from '@ipreo/muni-shared';
import {
  OffsetPosition,
  PositionWithDimension
} from '../models/position-types';

@Injectable()
export class PositionService {
  public position(nativeEl: HTMLElement): PositionWithDimension {
    const elBCR = this.offset(nativeEl);
    let offsetParentBCR = { top: 0, left: 0 };
    const offsetParentEl = this.parentOffsetEl(nativeEl);

    if (offsetParentEl !== (this.document as any)) {
      offsetParentBCR = this.offset(offsetParentEl);
      offsetParentBCR.top +=
        offsetParentEl.clientTop - offsetParentEl.scrollTop;
      offsetParentBCR.left +=
        offsetParentEl.clientLeft - offsetParentEl.scrollLeft;
    }

    const boundingClientRect = nativeEl.getBoundingClientRect();

    return {
      width: boundingClientRect.width || nativeEl.offsetWidth,
      height: boundingClientRect.height || nativeEl.offsetHeight,
      top: elBCR.top - offsetParentBCR.top,
      left: elBCR.left - offsetParentBCR.left
    };
  }

  public offset(nativeEl: HTMLElement): PositionWithDimension {
    const boundingClientRect: ClientRect = nativeEl.getBoundingClientRect();

    return {
      width: boundingClientRect.width || nativeEl.offsetWidth,
      height: boundingClientRect.height || nativeEl.offsetHeight,
      top:
        boundingClientRect.top +
        (this.window.pageYOffset || this.document.documentElement.scrollTop),
      left:
        boundingClientRect.left +
        (this.window.pageXOffset || this.document.documentElement.scrollLeft)
    };
  }

  public positionElements(
    hostEl: HTMLElement,
    targetEl: HTMLElement,
    positionStr: string,
    appendToBody: boolean
  ): OffsetPosition {
    const positionStrParts = positionStr.split('-');
    const pos0 = positionStrParts[0];
    const pos1 = positionStrParts[1] || 'center';
    const hostElPos = appendToBody
      ? this.offset(hostEl)
      : this.position(hostEl);
    const targetElWidth = targetEl.offsetWidth;
    const targetElHeight = targetEl.offsetHeight;
    const shiftWidth: KeyValue<any> = {
      center(): number {
        return hostElPos.left + hostElPos.width / 2 - targetElWidth / 2;
      },
      left(): number {
        return hostElPos.left;
      },
      right(): number {
        return hostElPos.left + hostElPos.width;
      }
    };

    const shiftHeight: KeyValue<any> = {
      center(): number {
        return hostElPos.top + hostElPos.height / 2 - targetElHeight / 2;
      },
      top(): number {
        return hostElPos.top;
      },
      bottom(): number {
        return hostElPos.top + hostElPos.height;
      }
    };

    let targetElPos: OffsetPosition;

    switch (pos0) {
      case 'right':
        targetElPos = {
          top: shiftHeight[pos1](),
          left: shiftWidth[pos0]()
        };
        break;
      case 'left':
        targetElPos = {
          top: shiftHeight[pos1](),
          left: hostElPos.left - targetElWidth
        };
        break;
      case 'bottom':
        targetElPos = {
          top: shiftHeight[pos0](),
          left: shiftWidth[pos1]()
        };
        break;
      default:
        targetElPos = {
          top: hostElPos.top - targetElHeight,
          left: shiftWidth[pos1]()
        };
        break;
    }

    return targetElPos;
  }

  protected get window(): Window {
    return window;
  }

  protected get document(): Document {
    return this.window.document;
  }

  protected getStyle(nativeEl: HTMLElement, cssProp: string): string {
    // IE
    if ((nativeEl as any).currentStyle) {
      return (nativeEl as any).currentStyle[cssProp];
    }

    if (this.window.getComputedStyle) {
      return (this.window.getComputedStyle(nativeEl) as KeyValue<any>)[cssProp];
    }

    // finally try and get inline style
    return (nativeEl.style as KeyValue<any>)[cssProp];
  }

  protected isStaticPositioned(nativeEl: HTMLElement): boolean {
    return (this.getStyle(nativeEl, 'position') || 'static') === 'static';
  }

  protected parentOffsetEl(nativeEl: HTMLElement): any {
    let offsetParent: any = nativeEl.offsetParent || this.document;

    while (
      offsetParent &&
      offsetParent !== this.document &&
      this.isStaticPositioned(offsetParent)
    ) {
      offsetParent = offsetParent.offsetParent;
    }

    return offsetParent || this.document;
  }
}
